import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Loader, Modal, Button } from "semantic-ui-react"; // Import Modal and Button
import "semantic-ui-css/semantic.min.css"; // Import Semantic UI styles
import FileIcon from "../../../SVGs/File.svg";
import Download from "../../../SVGs/Download.svg";
import Dash from "../../../SVGs/Dash.svg";
import Arrow from "../../../SVGs/Dropdown.svg";
import {
  getApiClient,
  makeStandardApiErrorHandler,
} from "../../../server/get_api_client";
import PdfViewerComponent from "../../Common/PdfViewerComponent";
import { Input } from "semantic-ui-react";
import toast from "react-hot-toast";

function FileRow({ data, setIsLoading }) {
  const [open, setOpen] = useState(false);
  const [document, setDocument] = useState(false);
  const [file, setFile] = useState(data.name);
  const [children, setChildren] = useState();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false); // Modal state for file upload
  const [selectedFiles, setSelectedFiles] = useState([]); // Selected files array
  const [createFolderModalOpen, setCreateFolderModalOpen] = useState(false); // Modal state for creating a folder
  const [newFolderName, setNewFolderName] = useState(""); // New folder name
  const [fileError, setFileError] = useState(""); // File validation error message
  const [uploadProgress, setUploadProgress] = useState({}); // Upload progress tracking
  const [isUploading, setIsUploading] = useState(false); // Upload in progress flag

  useEffect(() => {
    if (open) {
      setLoading(true);
      getApiClient()
        .getFolders({ location: file })
        .then((res) => {
          setChildren(res.data.children);
        })
        .finally(() => setLoading(false));
    }
  }, [open, file]);
  useEffect(() => {
    if (file === "1. Upload for Review by Hippocratic") {
      setOpen(true);
    }
  }, [file]);
  const handleBack = () => {
    if (history.length > 0) {
      const prevFile = history[history.length - 1];
      setHistory(history.slice(0, -1));
      setFile(prevFile);
    }
  };

  const download = (url) => {
    // setDocument(url);
    window.open(url, "_blank");
  };

  const validateFileType = (file) => {
    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/bmp',
      'image/webp',
      'image/tiff',
      'image/svg+xml',
      'text/plain',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];

    const fileExtension = file.name.toLowerCase().split('.').pop();
    const allowedExtensions = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'tiff', 'tif', 'svg', 'txt', 'doc', 'docx', 'xlsx', 'csv'];

    return allowedTypes.includes(file.type) || allowedExtensions.includes(fileExtension);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setFileError("");

    if (files.length > 0) {
      const validFiles = [];
      const invalidFiles = [];

      files.forEach(file => {
        if (validateFileType(file)) {
          validFiles.push(file);
        } else {
          invalidFiles.push(file.name);
        }
      });

      if (invalidFiles.length > 0) {
        setFileError(`Invalid file types: ${invalidFiles.join(', ')}. Please select only PDF, image, text, or Word files (JPG, PNG, GIF, BMP, WEBP, TIFF, SVG, TXT, DOC, DOCX, XLSX, CSV)`);
      }

      setSelectedFiles(validFiles);
    } else {
      setSelectedFiles([]);
    }
  };

  const removeFile = (indexToRemove) => {
    setSelectedFiles(prev => prev.filter((_, index) => index !== indexToRemove));
    setFileError("");
  };

  const uploadSingleFile = (fileToUpload) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        const base64Content = reader.result.split(",")[1];
        const payload = {
          location: file,
          fileName: fileToUpload.name,
          fileContent: base64Content,
        };

        getApiClient()
          .uploadFile(payload)
          .then(() => {
            setUploadProgress(prev => ({
              ...prev,
              [fileToUpload.name]: 'completed'
            }));
            resolve();
          })
          .catch((error) => {
            setUploadProgress(prev => ({
              ...prev,
              [fileToUpload.name]: 'error'
            }));
            reject(error);
          });
      };

      reader.onerror = (error) => {
        setUploadProgress(prev => ({
          ...prev,
          [fileToUpload.name]: 'error'
        }));
        reject(error);
      };

      reader.readAsDataURL(fileToUpload);
    });
  };

  const handleUploadFiles = async () => {
    if (selectedFiles.length > 0) {
      setIsLoading(true); // Show page loader
      setIsUploading(true);

      // Initialize progress tracking
      const initialProgress = {};
      selectedFiles.forEach(fileToUpload => {
        initialProgress[fileToUpload.name] = 'pending';
      });
      setUploadProgress(initialProgress);

      try {
        // Upload files sequentially
        for (let i = 0; i < selectedFiles.length; i++) {
          const fileToUpload = selectedFiles[i];
          setUploadProgress(prev => ({
            ...prev,
            [fileToUpload.name]: 'uploading'
          }));

          try {
            await uploadSingleFile(fileToUpload);
          } catch (error) {
            console.error(`Error uploading ${fileToUpload.name}:`, error);
            toast.error(`Failed to upload ${fileToUpload.name}`);
          }
        }

        // Refresh the folder view after all uploads
        setSelectedFiles([]);
        setUploadModalOpen(false);
        setFileError("");
        setUploadProgress({});
        setOpen(false);
        setTimeout(() => {
          setOpen(true);
        }, 300);

        toast.success(`Successfully uploaded ${selectedFiles.length} file(s)`);

      } catch (error) {
        console.error("Upload process error:", error);
      } finally {
        setIsLoading(false); // Hide page loader
        setIsUploading(false);
      }
    }
  };

  const handleCreateFolder = () => {
    if (newFolderName.trim()) {
      const payload = {
        location: file,
        folderName: newFolderName,
      };

      getApiClient()
        .createFolder(payload)
        .then(() => {
          console.log(`Folder "${newFolderName}" created successfully.`);
          setNewFolderName("");
          setCreateFolderModalOpen(false);
          setOpen(false);
          setTimeout(() => {
            setOpen(true);
          }, 300);
        })
        .catch(makeStandardApiErrorHandler((error) => console.error(error)));
    }
  };

  return (
    <Row>
      {loading && (
        <LoaderContainer>
          <Loader active inline="centered" />
        </LoaderContainer>
      )}

      <Section>
        <Image src={FileIcon} />
        <FileName>

          <FileName onClick={() => setOpen(!open)}>
            {file} <Image src={Arrow} />
          </FileName>
          {history.length !== 0 && (
            <BackButton onClick={handleBack} disabled={history.length === 0}>
              Back
            </BackButton>
          )}
        </FileName>
      </Section>



      {open &&
        children &&
        children.map((child) => (
          <SubSection
            onClick={
              child.type === "Folder"
                ? () => {
                  setHistory([...history, file]);
                  setFile(file + "/" + child.name);
                }
                : () => download(child.downloadUrl)
            }
            key={child.name}
          >
            <Image src={Dash} />
            <FileName>
              {child.type === "Folder" && <Image src={FileIcon} />}
              {child.name}
            </FileName>
            {child.downloadUrl && <Image src={Download} />}
          </SubSection>
        ))}
      {open && children && (
        <UploadSection>
          <CustomButton onClick={() => setUploadModalOpen(true)}>
            + Add File
          </CustomButton>
          <CustomButton onClick={() => setCreateFolderModalOpen(true)}>
            New Folder
          </CustomButton>
        </UploadSection>
      )}
      {document && <PdfViewerComponent document={document} />}
      <Modal
        open={uploadModalOpen}
        onClose={() => {
          setUploadModalOpen(false);
          setFileError("");
          setSelectedFiles([]);
          setUploadProgress({});
        }}
        size="mini"
      >
        <Modal.Header>Upload Files</Modal.Header>
        <Modal.Content>
          <div style={{ marginBottom: "10px" }}>
            <input
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.gif,.bmp,.webp,.tiff,.tif,.svg,.txt,.doc,.docx,.xlsx,.csv"
              onChange={handleFileChange}
              style={{ width: "100%" }}
            />
            {fileError && (
              <div style={{
                color: "red",
                fontSize: "12px",
                marginTop: "5px",
                fontFamily: "Museo Sans Cyrl"
              }}>
                {fileError}
              </div>
            )}
            <div style={{
              fontSize: "12px",
              color: "#666",
              marginTop: "5px",
              fontFamily: "Museo Sans Cyrl"
            }}>
              Accepted formats: PDF, JPG, PNG, GIF, BMP, WEBP, TIFF, SVG, TXT, DOC, DOCX, XLSX, CSV
            </div>

            {/* Selected Files List */}
            {selectedFiles.length > 0 && (
              <div style={{ marginTop: "15px" }}>
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "8px"
                }}>
                  <div style={{
                    fontSize: "14px",
                    fontWeight: "bold",
                    fontFamily: "Museo Sans Cyrl"
                  }}>
                    Selected Files ({selectedFiles.length}):
                  </div>
                  {!isUploading && (
                    <button
                      onClick={() => setSelectedFiles([])}
                      style={{
                        background: "none",
                        border: "1px solid #dc3545",
                        color: "#dc3545",
                        cursor: "pointer",
                        fontSize: "11px",
                        padding: "2px 6px",
                        borderRadius: "3px",
                        fontFamily: "Museo Sans Cyrl"
                      }}
                      title="Clear all files"
                    >
                      Clear All
                    </button>
                  )}
                </div>
                {selectedFiles.map((file, index) => (
                  <div key={index} style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "5px 0",
                    borderBottom: "1px solid #eee",
                    fontFamily: "Museo Sans Cyrl",
                    fontSize: "12px"
                  }}>
                    <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {file.name}
                    </span>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <span style={{
                        fontSize: "10px",
                        color: uploadProgress[file.name] === 'completed' ? 'green' :
                          uploadProgress[file.name] === 'error' ? 'red' :
                            uploadProgress[file.name] === 'uploading' ? 'blue' : '#666'
                      }}>
                        {uploadProgress[file.name] === 'completed' ? '✓' :
                          uploadProgress[file.name] === 'error' ? '✗' :
                            uploadProgress[file.name] === 'uploading' ? '⏳' : '○'}
                      </span>
                      {!isUploading && (
                        <button
                          onClick={() => removeFile(index)}
                          style={{
                            background: "none",
                            border: "none",
                            color: "#dc3545",
                            cursor: "pointer",
                            fontSize: "14px",
                            padding: "2px 4px",
                            borderRadius: "3px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                          }}
                          title="Remove file"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setUploadModalOpen(false)}>Cancel</Button>
          <Button
            color="blue"
            onClick={handleUploadFiles}
            disabled={selectedFiles.length === 0 || fileError || isUploading}
            loading={isUploading}
          >
            {isUploading ? 'Uploading...' : `Upload ${selectedFiles.length} File${selectedFiles.length !== 1 ? 's' : ''}`}
          </Button>
        </Modal.Actions>
      </Modal>
      <Modal
        open={createFolderModalOpen}
        onClose={() => setCreateFolderModalOpen(false)}
        size="mini"
      >
        <Modal.Header>Create New Folder</Modal.Header>
        <Modal.Content>
          <Input
            style={{ width: "100%" }}
            type="text"
            placeholder="Folder Name"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
          />
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setCreateFolderModalOpen(false)}>
            Cancel
          </Button>
          <Button
            color="blue"
            onClick={handleCreateFolder}
            disabled={!newFolderName.trim()}
          >
            Create
          </Button>
        </Modal.Actions>
      </Modal>
    </Row>
  );
}

const LoaderContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 5px; /* Match Row's border radius */
  z-index: 10; /* Ensure loader is above content */
`;

const CustomButton = styled.div`
  cursor: pointer;
  text-align: center;
  border-radius: 300px;
  background: #304961;
  color: white;
  padding: 5px 0px;
`;

const Image = styled.img`
  cursor: pointer;
`;

const Section = styled.div`
  display: grid;
  align-items: center;
  padding: 0px 15px;
  grid-template-columns: 30px 2fr;
  grid-template-rows: repeat(auto-fit, minmax(40px, auto));
`;

const SubSection = styled.div`
  display: grid;
  align-items: center;
  padding: 0px;
  padding-right: 15px;
  margin-left: 48px;
  border-left: 1px dashed #dbe8f3;
  grid-template-columns: 30px 2fr 50px;
  grid-template-rows: repeat(auto-fit, minmax(30px, auto));
  border-bottom: solid 1px #dbe8f3;
`;

const UploadSection = styled.div`
  display: grid;
  gap: 10px;
  align-items: center;
  padding: 10px 0px;
  padding-left: 20px;
  margin-left: 48px;
  border-left: 1px dashed #dbe8f3;
  grid-template-columns: 100px 100px;
  border-bottom: solid 1px #dbe8f3;

  @media screen and (max-width: 371px) {
    grid-template-columns: 80px 80px;
  }
`;

const FileName = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  color: #304961;
  font-family: "Museo Sans Cyrl";
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  letter-spacing: -0.28px;
`;

const Row = styled.div`
  position: relative; /* Position relative to contain loader */
  background-color: white;
  border-radius: 5px;
  overflow: hidden; /* Prevent overflow when loader is displayed */
`;

const BackButton = styled.button`
  cursor: pointer;
  margin-left: 10px;
  background-color: #dbe8f3;
  border: none;
  border-radius: 3px;
  padding: 5px 10px;
  font-size: 14px;
  font-weight: bold;
  color: #304961;
  &:disabled {
    background-color: #f2f2f2;
    cursor: not-allowed;
    color: #a0a0a0;
  }
`;

export default FileRow;
